export interface MaterialsWorkshopAssetRequirement {
  readonly key: string;
  readonly path: string;
  readonly width: number;
  readonly height: number;
  readonly kind: 'image';
  readonly alpha: boolean;
  readonly alignedCanvasGroup?: string;
  readonly runtimeReady: true;
}

const requiredImage = (
  key: string,
  path: string,
  width: number,
  height: number,
  alpha: boolean,
  alignedCanvasGroup?: string,
): MaterialsWorkshopAssetRequirement => ({
  key,
  path,
  width,
  height,
  kind: 'image',
  alpha,
  alignedCanvasGroup,
  runtimeReady: true,
});

const ROOT = 'assets/discovery-lab/materials-workshop';
const materialState = (material: string, state: string) => requiredImage(
  `materials-workshop-${material}-${state}`,
  `${ROOT}/materials/${material}-${state}.png`,
  384,
  384,
  true,
  `materials-workshop-${material}`,
);

/** Authoritative runtime registry for the validated production pack. */
export const MATERIALS_WORKSHOP_ASSET_REQUIREMENTS = [
  requiredImage('materials-workshop-background', `${ROOT}/backgrounds/materials-workshop-bg.png`, 1280, 720, false),
  requiredImage('materials-workshop-workbench', `${ROOT}/environment/workbench.png`, 1280, 720, true, 'materials-workshop-environment'),
  requiredImage('materials-workshop-shelter-damaged', `${ROOT}/environment/shelter-damaged.png`, 640, 480, true, 'materials-workshop-shelter'),
  requiredImage('materials-workshop-shelter-repaired', `${ROOT}/environment/shelter-repaired.png`, 640, 480, true, 'materials-workshop-shelter'),
  requiredImage('materials-workshop-foreground', `${ROOT}/environment/workshop-foreground.png`, 1280, 720, true, 'materials-workshop-environment'),

  materialState('fiber-sheet', 'base'),
  materialState('fiber-sheet', 'water-test'),
  materialState('fiber-sheet', 'bend-test'),
  materialState('fiber-sheet', 'light-test'),
  materialState('sealed-wood-panel', 'base'),
  materialState('sealed-wood-panel', 'water-test'),
  materialState('sealed-wood-panel', 'bend-test'),
  materialState('sealed-wood-panel', 'light-test'),
  materialState('clear-polymer-sheet', 'base'),
  materialState('clear-polymer-sheet', 'water-test'),
  materialState('clear-polymer-sheet', 'bend-test'),
  materialState('clear-polymer-sheet', 'light-test'),

  requiredImage('materials-workshop-water-station', `${ROOT}/interactives/water-test-station.png`, 512, 512, true),
  requiredImage('materials-workshop-bend-station', `${ROOT}/interactives/bend-test-station.png`, 512, 512, true),
  requiredImage('materials-workshop-light-station', `${ROOT}/interactives/light-test-station.png`, 512, 512, true),
  requiredImage('materials-workshop-evidence-marker', `${ROOT}/interactives/property-evidence-marker.png`, 192, 192, true),
  requiredImage('materials-workshop-build-station', `${ROOT}/interactives/build-apply-station.png`, 512, 512, true),

  requiredImage('materials-workshop-mission-panel', `${ROOT}/ui/mission-panel.png`, 720, 240, true),
  requiredImage('materials-workshop-observation-panel', `${ROOT}/ui/observation-evidence-panel.png`, 720, 420, true),
  requiredImage('materials-workshop-test-button', `${ROOT}/ui/test-button.png`, 320, 160, true),
  requiredImage('materials-workshop-hint-button', `${ROOT}/ui/hint-button.png`, 256, 128, true),
  requiredImage('materials-workshop-reset-button', `${ROOT}/ui/reset-button.png`, 256, 128, true),
  requiredImage('materials-workshop-apply-button', `${ROOT}/ui/apply-material-button.png`, 384, 192, true),

  requiredImage('materials-workshop-test-feedback-vfx', `${ROOT}/vfx/test-feedback-vfx.png`, 640, 480, true),
  requiredImage('materials-workshop-selection-vfx', `${ROOT}/vfx/material-selection-confirmation-vfx.png`, 640, 480, true),
  requiredImage('materials-workshop-restoration-vfx', `${ROOT}/vfx/shelter-restoration-vfx.png`, 960, 540, true),
] as const;

export const MATERIALS_WORKSHOP_PRODUCTION_READY =
  MATERIALS_WORKSHOP_ASSET_REQUIREMENTS.every(asset => asset.runtimeReady) as boolean;

export function preloadMaterialsWorkshopAssets(scene: Phaser.Scene) {
  MATERIALS_WORKSHOP_ASSET_REQUIREMENTS.forEach(asset => scene.load.image(asset.key, asset.path));
}

export function getMissingMaterialsWorkshopAssets(textures: Phaser.Textures.TextureManager) {
  return MATERIALS_WORKSHOP_ASSET_REQUIREMENTS.filter(asset => !textures.exists(asset.key));
}

export function canEnterMaterialsWorkshop(textures: Phaser.Textures.TextureManager) {
  return MATERIALS_WORKSHOP_PRODUCTION_READY
    && getMissingMaterialsWorkshopAssets(textures).length === 0;
}
